import React, { useEffect, useRef, useState, useCallback } from 'react';
import { LockOutlined } from '@ant-design/icons';
import { IBrandEquityScoreResponse } from '../../services/nestjs-base-api/types';

interface RadialOrbitalVisualizationProps {
    results: IBrandEquityScoreResponse;
    isModal?: boolean;
    onElementClick?: (elementName: string, elementData: any) => void;
    elementDescriptions?: Record<string, { mouseover?: string; sidebar?: string }>;
    focusedElementName?: string | null;
}

interface Node {
    element: string;
    score: number;
    level: number;
    x: number;
    y: number;
    targetX: number;
    targetY: number;
    radius: number;
    color: { r: number; g: number; b: number };
    baseRadius: number;
    angle: number;
    targetAngle: number;
    pulsePhase: number;
    hovered: boolean;
    selected: boolean;
    index: number;
    totalAtLevel: number;
    locked: boolean;
}

interface Connection {
    from: string;
    to: string;
}

interface Particle {
    from: Node;
    to: Node;
    progress: number;
    speed: number;
    size: number;
    opacity: number;
}

interface Config {
    showParticles: boolean;
    lightMode: boolean;
    zoom: number;
}

interface PanState {
    offsetX: number;
    offsetY: number;
    isDragging: boolean;
    hasMoved: boolean;  // Track if mouse actually moved during drag
    lastX: number;
    lastY: number;
}

interface TouchState {
    lastTouchDistance: number | null;
    lastTouchCenter: { x: number; y: number } | null;
}

// Zoom constraints
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.1;

const RadialOrbitalVisualization: React.FC<RadialOrbitalVisualizationProps> = ({
    results,
    isModal = false,
    onElementClick,
    elementDescriptions = {},
    focusedElementName = null
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Load config from localStorage or use defaults
    const [config, setConfig] = useState<Config>(() => {
        try {
            const savedConfig = localStorage.getItem('radialOrbitalVisualizationConfig');
            if (savedConfig) {
                const parsed = JSON.parse(savedConfig);
                return {
                    showParticles: parsed.showParticles ?? true,
                    lightMode: parsed.lightMode ?? true,
                    zoom: 1 // Don't persist zoom
                };
            }
        } catch (e) {
            console.error('Failed to load visualization config from localStorage:', e);
        }
        return {
            showParticles: true,
            lightMode: true,
            zoom: 1
        };
    });

    // Save config to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem('radialOrbitalVisualizationConfig', JSON.stringify({
                showParticles: config.showParticles,
                lightMode: config.lightMode
                // Don't save zoom
            }));
        } catch (e) {
            console.error('Failed to save visualization config to localStorage:', e);
        }
    }, [config.showParticles, config.lightMode]);

    const nodesRef = useRef<Node[]>([]);
    const particlesRef = useRef<Particle[]>([]);
    const [hoveredNode, setHoveredNode] = useState<Node | null>(null);
    const animationFrameRef = useRef<number>();
    const lastTimeRef = useRef<number>(Date.now());
    const initFrameCountRef = useRef<number>(0);

    // Pan and zoom state
    const [pan, setPan] = useState<PanState>({
        offsetX: 0,
        offsetY: 0,
        isDragging: false,
        hasMoved: false,
        lastX: 0,
        lastY: 0
    });
    const touchStateRef = useRef<TouchState>({
        lastTouchDistance: null,
        lastTouchCenter: null
    });

    // Find focused node from focusedElementName
    const focusedNode = React.useMemo(() => {
        if (!focusedElementName) return null;
        return nodesRef.current.find(node => node.element === focusedElementName) || null;
    }, [focusedElementName]);

    // Build scores map from results
    const scoresMap = React.useMemo(() => {
        const map: Record<string, number> = {};

        // Add overall brand equity score (default null to 0 like classic)
        map['Brand Equity'] = results.overallBrandEquityScore ?? 0;

        // Add latent element scores (default null to 0 like classic)
        results.latentElementScores.forEach(le => {
            map[le.element] = le.score ?? 0;
        });

        // Add question category scores (default null to 0 like classic)
        results.questionCategoryScores.forEach(qc => {
            map[qc.category] = qc.meanScore ?? 0;
        });

        return map;
    }, [results]);

    // Helper function to check if an element is locked
    const isElementLocked = useCallback((elementName: string): boolean => {
        const latentElement = results.latentElementScores.find(le => le.element === elementName);
        if (latentElement) return latentElement.locked === true;
        const qcElement = results.questionCategoryScores.find(qc => qc.category === elementName);
        if (qcElement) return qcElement.locked === true;
        return false;
    }, [results]);

    // Convert results data to node format
    const brandEquityData = React.useMemo(() => {
        const data: Array<{ element: string; score: number; level: number }> = [];

        // Add Brand Equity (top level)
        data.push({
            element: 'Brand Equity',
            score: scoresMap['Brand Equity'] || 0,
            level: 0
        });

        // Add Level 1 (primary latent elements)
        const level1Elements = ['Brand Loyalty', 'Perceived Quality', 'Associations', 'Market Awareness'];
        level1Elements.forEach(element => {
            const score = scoresMap[element];
            if (score !== undefined) {
                data.push({
                    element,
                    score: score,
                    level: 1
                });
            }
        });

        // Add Level 2 (secondary latent elements)
        const level2Elements = ['Attitudinal Loyalty', 'Behavioural Loyalty', 'Alignment Depth', 'Willingness to Pay a Premium', 'Brand Attachment', 'Quality Value Tradeoff (PQV)'];
        level2Elements.forEach(element => {
            const score = scoresMap[element];
            if (score !== undefined) {
                data.push({
                    element,
                    score: score,
                    level: 2
                });
            }
        });

        // Add Level 3 (measured elements - grouped by category for better visual organization)
        const level3Elements = [
            // Emotional Connection
            'Prominence',
            'Self Congruence',
            'User Congruence',
            'Brand Identification',
            'Brand Self Connection',
            // Brand Awareness
            'Symbolic Recall',
            'Advertising Intensity',
            'Brand Familiarity',
            'Distribution Intensity',
            // Repurchase and Recommend
            'Repurchase Intention',
            'Self Reported Loyalty',
            'Standardised NPS',
            'Parity Preference',
            // Interaction
            'Staff Behaviour',
            'Brand Environment',
            // Brand Personality
            'Uniqueness',
            'Advertising Content',
            'Brand Empathy',
            'Brand Trust',
            'Brand Valence',
            'Price Deals',
            // Rational Perception
            'Perceived Value for the Cost',
            'Price',
            'Product Performance',
            'Product Richness'
        ];

        level3Elements.forEach(element => {
            const score = scoresMap[element];
            if (score !== undefined) {
                data.push({
                    element,
                    score: score,
                    level: 3
                });
            }
        });

        // Add any remaining elements not in the predefined list
        Object.keys(scoresMap).forEach(element => {
            if (element !== 'Brand Equity' &&
                !level1Elements.includes(element) &&
                !level2Elements.includes(element) &&
                !level3Elements.includes(element)) {
                const score = scoresMap[element];
                if (score !== undefined) {
                    data.push({
                        element,
                        score: score,
                        level: 3
                    });
                }
            }
        });

        return data;
    }, [scoresMap]);

    // Extract connections from results
    const connectionData = React.useMemo(() => {
        const connections: Connection[] = [];

        // Build connections from latent element inputs
        results.latentElementScores.forEach(le => {
            if (le.inputs && le.inputs.length > 0) {
                le.inputs.forEach(input => {
                    connections.push({
                        from: input.name,
                        to: le.element
                    });
                });
            }
        });

        return connections;
    }, [results]);

    // Initialize nodes
    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        const levelCounts: Record<number, number> = {};
        brandEquityData.forEach(data => {
            levelCounts[data.level] = (levelCounts[data.level] || 0) + 1;
        });

        const levelIndices: Record<number, number> = {};
        const newNodes: Node[] = [];

        brandEquityData.forEach(data => {
            const level = data.level;
            levelIndices[level] = (levelIndices[level] || 0);

            const getBaseRadius = (level: number) => {
                const radiusMap: Record<number, number> = { 0: 0, 1: 180, 2: 280, 3: 400 };
                return radiusMap[level] || 400;
            };

            const getAngle = (index: number, total: number, level: number, elementName: string): number => {
                if (level === 0) return 0;

                // Manual positioning for level 2 elements to maintain original positions
                if (level === 2) {
                    const level2Positions: Record<string, number> = {
                        'Attitudinal Loyalty': Math.PI / 4,           // 45 degrees (original position 0)
                        'Behavioural Loyalty': 3 * Math.PI / 4,       // 135 degrees (original position 1)
                        'Alignment Depth': 5 * Math.PI / 4,           // 225 degrees (original position 2)
                        'Willingness to Pay a Premium': 7 * Math.PI / 4, // 315 degrees (original position 3)
                        'Quality Value Tradeoff (PQV)': Math.PI,      // 180 degrees / 9 o'clock (left)
                        'Brand Attachment': 0                          // 0 degrees / 3 o'clock (right)
                    };
                    return level2Positions[elementName] || (index / total) * Math.PI * 2 + Math.PI / 4;
                }

                return (index / total) * Math.PI * 2 + Math.PI / 4; // Add 45 degree rotation
            };

            const getNodeRadius = (level: number): number => {
                if (level === 0) return 80;
                if (level === 1) return 40;
                if (level === 2) return 30;
                return 22;
            };

            const getColor = (elementName: string, level: number, locked: boolean) => {
                // Return grey for locked elements
                if (locked) {
                    return { r: 156, g: 163, b: 175 }; // #9ca3af - grey
                }

                // Match classic visualization colors based on element type/name
                if (elementName === 'Brand Equity') {
                    return { r: 251, g: 191, b: 36 }; // #fbbf24 - golden/amber
                }

                // Check if latent element
                const isLatent = results.latentElementScores.some(le => le.element === elementName);

                if (isLatent) {
                    // Specific latent element colors
                    if (elementName === 'Brand Loyalty') {
                        return { r: 16, g: 185, b: 129 }; // #10b981 - green
                    } else if (elementName === 'Attitudinal Loyalty' ||
                               elementName === 'Perceived Quality' ||
                               elementName === 'Behavioural Loyalty') {
                        return { r: 251, g: 146, b: 60 }; // #fb923c - orange
                    } else {
                        return { r: 239, g: 68, b: 68 }; // #ef4444 - red (Associations, Awareness, etc.)
                    }
                } else {
                    // Question Categories / Measured Elements
                    return { r: 59, g: 130, b: 246 }; // #3b82f6 - blue
                }
            };

            const baseRadius = getBaseRadius(level);
            const angle = getAngle(levelIndices[level], levelCounts[level], level, data.element);
            const effectiveRadius = baseRadius * config.zoom;
            const locked = isElementLocked(data.element);

            const node: Node = {
                element: data.element,
                score: data.score,
                level: data.level,
                index: levelIndices[level],
                totalAtLevel: levelCounts[level],
                baseRadius,
                angle,
                targetAngle: angle,
                radius: getNodeRadius(level),
                color: getColor(data.element, level, locked),
                pulsePhase: Math.random() * Math.PI * 2,
                hovered: false,
                selected: false,
                locked,
                x: level === 0 ? centerX : centerX + Math.cos(angle) * effectiveRadius,
                y: level === 0 ? centerY : centerY + Math.sin(angle) * effectiveRadius,
                targetX: level === 0 ? centerX : centerX + Math.cos(angle) * effectiveRadius,
                targetY: level === 0 ? centerY : centerY + Math.sin(angle) * effectiveRadius
            };

            newNodes.push(node);
            levelIndices[level]++;
        });

        nodesRef.current = newNodes;

        // Clear particles - they'll be recreated after nodes settle (in animation loop)
        particlesRef.current = [];
        initFrameCountRef.current = 0;
    }, [brandEquityData, connectionData, config.zoom, results.latentElementScores, isElementLocked]);

    // Update node positions
    const updateNodes = useCallback((deltaTime: number) => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const centerX = canvas.width / 2 + pan.offsetX;
        const centerY = canvas.height / 2 + pan.offsetY;

        nodesRef.current = nodesRef.current.map(node => {
            // Smooth position interpolation for zoom changes
            const newX = node.x + (node.targetX - node.x) * 0.1;
            const newY = node.y + (node.targetY - node.y) * 0.1;

            // Calculate target position based on current zoom
            const effectiveRadius = node.baseRadius * config.zoom;
            const newTargetX = node.level === 0 ? centerX : centerX + Math.cos(node.angle) * effectiveRadius;
            const newTargetY = node.level === 0 ? centerY : centerY + Math.sin(node.angle) * effectiveRadius;

            return {
                ...node,
                x: newX,
                y: newY,
                targetX: newTargetX,
                targetY: newTargetY
            };
        });
    }, [config.zoom, pan.offsetX, pan.offsetY]);

    // Drawing functions
    const drawConnections = useCallback((ctx: CanvasRenderingContext2D) => {
        connectionData.forEach(conn => {
            const fromNode = nodesRef.current.find(n => n.element === conn.from);
            const toNode = nodesRef.current.find(n => n.element === conn.to);

            if (!fromNode || !toNode) return;

            const isHighlighted = (hoveredNode &&
                (fromNode.element === hoveredNode.element || toNode.element === hoveredNode.element)) ||
                (focusedNode &&
                (fromNode.element === focusedNode.element || toNode.element === focusedNode.element));

            // Only show connections on hover or focus
            if (!isHighlighted) return;

            ctx.strokeStyle = 'rgba(147, 197, 253, 0.8)';
            ctx.lineWidth = 3;

            ctx.beginPath();
            ctx.moveTo(fromNode.x, fromNode.y);
            ctx.lineTo(toNode.x, toNode.y);
            ctx.stroke();
        });
    }, [connectionData, hoveredNode, focusedNode]);

    const drawParticles = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number) => {
        if (!config.showParticles) return;

        particlesRef.current = particlesRef.current.map(particle => {
            let newProgress = particle.progress + (particle.speed * deltaTime / 16);
            if (newProgress > 1) newProgress = 0;

            // Look up current node positions from nodesRef instead of using stale references
            const fromNode = nodesRef.current.find(n => n.element === particle.from.element);
            const toNode = nodesRef.current.find(n => n.element === particle.to.element);

            if (!fromNode || !toNode) return { ...particle, progress: newProgress };

            const x = fromNode.x + (toNode.x - fromNode.x) * newProgress;
            const y = fromNode.y + (toNode.y - fromNode.y) * newProgress;

            const gradient = ctx.createRadialGradient(x, y, 0, x, y, particle.size * 2);
            gradient.addColorStop(0, `rgba(147, 197, 253, ${particle.opacity})`);
            gradient.addColorStop(1, 'rgba(147, 197, 253, 0)');

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(x, y, particle.size * 2, 0, Math.PI * 2);
            ctx.fill();

            return { ...particle, progress: newProgress };
        });
    }, [config.showParticles]);

    const drawNodes = useCallback((ctx: CanvasRenderingContext2D) => {
        nodesRef.current.forEach(node => {
            // Scale node size with zoom for a true zoom effect
            const zoomScale = config.zoom;
            const displayRadius = node.radius * zoomScale * (node.hovered ? 1.3 : 1);

            // Check if this node is connected to the hovered or focused node
            const isConnectedToHovered = hoveredNode && hoveredNode.element !== node.element &&
                connectionData.some(conn =>
                    (conn.from === hoveredNode.element && conn.to === node.element) ||
                    (conn.to === hoveredNode.element && conn.from === node.element)
                );

            const isConnectedToFocused = focusedNode && focusedNode.element !== node.element &&
                connectionData.some(conn =>
                    (conn.from === focusedNode.element && conn.to === node.element) ||
                    (conn.to === focusedNode.element && conn.from === node.element)
                );

            const isConnected = isConnectedToHovered || isConnectedToFocused;

            // Determine if this node should be dimmed (not hovered/focused and not connected)
            const activeNode = hoveredNode || focusedNode;
            const shouldDim = activeNode && activeNode.element !== node.element && !isConnected;
            const opacity = shouldDim ? 0.3 : 1;

            // Save context and apply opacity
            ctx.save();
            ctx.globalAlpha = opacity;

            // Special glow for Brand Equity (level 0)
            if (node.level === 0) {
                const haloGradient = ctx.createRadialGradient(
                    node.x, node.y, 0,
                    node.x, node.y, displayRadius * 3
                );
                haloGradient.addColorStop(0, `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, 0.4)`);
                haloGradient.addColorStop(0.5, `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, 0.2)`);
                haloGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

                ctx.fillStyle = haloGradient;
                ctx.beginPath();
                ctx.arc(node.x, node.y, displayRadius * 3, 0, Math.PI * 2);
                ctx.fill();
            }

            // Outer glow
            if (node.hovered || node.selected) {
                const gradient = ctx.createRadialGradient(
                    node.x, node.y, 0,
                    node.x, node.y, displayRadius * 2
                );
                gradient.addColorStop(0, `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, 0.3)`);
                gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(node.x, node.y, displayRadius * 2, 0, Math.PI * 2);
                ctx.fill();
            }

            // Main circle
            const gradient = ctx.createRadialGradient(
                node.x - displayRadius * 0.3,
                node.y - displayRadius * 0.3,
                0,
                node.x,
                node.y,
                displayRadius
            );
            gradient.addColorStop(0, `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, 1)`);
            gradient.addColorStop(1, `rgba(${node.color.r * 0.7}, ${node.color.g * 0.7}, ${node.color.b * 0.7}, 1)`);

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(node.x, node.y, displayRadius, 0, Math.PI * 2);
            ctx.fill();

            // Extra rings for Brand Equity
            if (node.level === 0) {
                ctx.strokeStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, 0.6)`;
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.arc(node.x, node.y, displayRadius + 8, 0, Math.PI * 2);
                ctx.stroke();

                ctx.strokeStyle = `rgba(${node.color.r}, ${node.color.g}, ${node.color.b}, 0.3)`;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(node.x, node.y, displayRadius + 16, 0, Math.PI * 2);
                ctx.stroke();
            }

            // Border - highlight if connected to hovered node
            if (isConnected) {
                ctx.strokeStyle = config.lightMode ? 'rgba(59, 130, 246, 1)' : 'rgba(147, 197, 253, 1)'; // Darker blue in light mode
                ctx.lineWidth = 3;
            } else {
                ctx.strokeStyle = node.level === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)';
                ctx.lineWidth = node.level === 0 ? 3 : 2;
            }
            ctx.beginPath();
            ctx.arc(node.x, node.y, displayRadius, 0, Math.PI * 2);
            ctx.stroke();

            // Score text - show lock icon for locked elements
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            if (node.locked) {
                // For locked nodes, draw the Ant Design LockOutlined icon
                // Larger icons for larger circles (level 0 and 1)
                const lockSize = node.level === 0 ? displayRadius * 0.55 : node.level === 1 ? displayRadius * 0.5 : Math.max(12, displayRadius * 0.4);

                ctx.save();
                ctx.translate(node.x, node.y);

                // Scale factor to fit the icon (Ant Design icon is in 1024x1024 viewBox)
                const scale = lockSize / 512;
                ctx.scale(scale, scale);
                ctx.translate(-512, -512); // Center the icon

                ctx.fillStyle = '#6b7280';

                // Ant Design LockOutlined SVG path
                const lockPath = new Path2D('M832 464h-68V240c0-70.7-57.3-128-128-128H388c-70.7 0-128 57.3-128 128v224h-68c-17.7 0-32 14.3-32 32v384c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V496c0-17.7-14.3-32-32-32zM332 240c0-30.9 25.1-56 56-56h248c30.9 0 56 25.1 56 56v224H332V240zm460 600H232V536h560v304zM484 701v53c0 4.4 3.6 8 8 8h40c4.4 0 8-3.6 8-8v-53a48.01 48.01 0 10-56 0z');
                ctx.fill(lockPath);

                ctx.restore();
            } else {
                ctx.fillStyle = 'white';
                ctx.font = `bold ${Math.max(12, node.level === 0 ? displayRadius * 0.6 : displayRadius * 0.5)}px sans-serif`;
                ctx.fillText(`${Math.round(node.score)}%`, node.x, node.y);
            }

            // Label - scale font size with zoom
            ctx.fillStyle = config.lightMode ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255, 255, 255, 0.9)';
            const baseLabelSize = node.level === 0 ? 16 : node.level < 3 ? 11 : 10;
            ctx.font = `600 ${Math.round(baseLabelSize * zoomScale)}px sans-serif`;
            ctx.textAlign = 'center';

            // Apply custom line breaks for specific elements to prevent overlap
            let labelText = node.element;
            if (node.element === 'Willingness to Pay a Premium') {
                labelText = 'Willingness\nto Pay a\nPremium';
            } else if (node.element === 'Alignment Depth') {
                labelText = 'Alignment\nDepth';
            } else if (node.element === 'Perceived Quality') {
                labelText = 'Perceived\nQuality';
            }

            // Check if we have custom line breaks
            let lines: string[] = [];
            if (labelText.includes('\n')) {
                // Use custom line breaks
                lines = labelText.split('\n');
            } else {
                // Use automatic word wrapping
                const words = labelText.split(' ');
                const maxWidth = (node.level === 0 ? 150 : 120) * zoomScale;
                let currentLine = '';

                words.forEach(word => {
                    const testLine = currentLine + (currentLine ? ' ' : '') + word;
                    const metrics = ctx.measureText(testLine);
                    if (metrics.width > maxWidth && currentLine) {
                        lines.push(currentLine);
                        currentLine = word;
                    } else {
                        currentLine = testLine;
                    }
                });
                if (currentLine) lines.push(currentLine);
            }

            const lineHeight = (node.level === 0 ? 18 : 14) * zoomScale;
            const startY = node.y + displayRadius + (node.level === 0 ? 20 : 15) * zoomScale;
            lines.forEach((line, i) => {
                ctx.fillText(line, node.x, startY + i * lineHeight);
            });

            // Restore context
            ctx.restore();
        });
    }, [connectionData, hoveredNode, focusedNode, config.lightMode, config.zoom]);

    // Animation loop
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const animate = () => {
            const now = Date.now();
            const deltaTime = now - lastTimeRef.current;
            lastTimeRef.current = now;

            // Clear canvas with appropriate background
            ctx.fillStyle = config.lightMode ? '#f8fafc' : '#0a0e27';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Update nodes
            updateNodes(deltaTime);

            // Initialize particles after more frames to ensure canvas is properly sized and nodes settled
            initFrameCountRef.current++;
            if (initFrameCountRef.current === 60 && particlesRef.current.length === 0 && nodesRef.current.length > 0) {
                const newParticles: Particle[] = [];
                connectionData.forEach(conn => {
                    const fromNode = nodesRef.current.find(n => n.element === conn.from);
                    const toNode = nodesRef.current.find(n => n.element === conn.to);
                    if (fromNode && toNode) {
                        for (let i = 0; i < 2; i++) {
                            newParticles.push({
                                from: fromNode,
                                to: toNode,
                                progress: Math.random(),
                                speed: 0.005 + Math.random() * 0.005,
                                size: 2 + Math.random() * 2,
                                opacity: 0.8
                            });
                        }
                    }
                });
                particlesRef.current = newParticles;
            }

            // Draw connections
            drawConnections(ctx);

            // Draw particles
            drawParticles(ctx, deltaTime);

            // Draw nodes
            drawNodes(ctx);

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [updateNodes, drawConnections, drawParticles, drawNodes, config.lightMode, connectionData]);

    // Get element type and label
    const getElementTypeLabel = useCallback((elementName: string) => {
        if (elementName === 'Brand Equity') return 'Brand Equity';
        const isLatent = results.latentElementScores.some(le => le.element === elementName);
        return isLatent ? 'Latent Element' : 'Measured Element';
    }, [results]);

    // Get score color
    const getScoreColor = useCallback((elementName: string) => {
        if (elementName === 'Brand Equity') return '#fbbf24';

        const isLatent = results.latentElementScores.some(le => le.element === elementName);
        if (isLatent) {
            if (elementName === 'Attitudinal Loyalty' || elementName === 'Perceived Quality' || elementName === 'Behavioural Loyalty') {
                return '#fb923c';
            }
            if (elementName === 'Brand Loyalty') {
                return '#10b981';
            }
            return '#ef4444';
        }
        return '#3b82f6';
    }, [results]);

    // Handle mouse move
    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        let foundHover = false;
        let hoveredNodeFound: Node | null = null;

        nodesRef.current = nodesRef.current.map(node => {
            const dx = x - node.x;
            const dy = y - node.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            // Account for zoom when detecting hover
            const hitRadius = node.radius * config.zoom * 1.3;
            const isHovered = distance < hitRadius;

            if (isHovered) {
                foundHover = true;
                hoveredNodeFound = node;
            }

            return { ...node, hovered: isHovered };
        });

        setHoveredNode(hoveredNodeFound);
        canvas.style.cursor = foundHover ? 'pointer' : 'default';
    }, [config.zoom]);

    // Handle click
    const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        // Don't trigger click if we were panning (moved the mouse during drag)
        if (pan.hasMoved) return;

        if (hoveredNode && onElementClick) {
            onElementClick(hoveredNode.element, hoveredNode);
            // Clear hover state after click so only focused element shows
            setHoveredNode(null);
        }
    }, [hoveredNode, onElementClick, pan.hasMoved]);

    // Mouse wheel zoom removed - use buttons instead to avoid interfering with page scroll

    // Handle mouse down for pan - allow drag from anywhere (click vs drag distinguished by movement)
    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        // Left click starts potential pan (we'll distinguish click from drag by movement)
        if (e.button === 0 || e.button === 1) {
            setPan(prev => ({
                ...prev,
                isDragging: true,
                hasMoved: false,  // Reset movement tracking
                lastX: e.clientX,
                lastY: e.clientY
            }));
        }
    }, []);

    // Handle mouse up for pan
    const handleMouseUp = useCallback(() => {
        setPan(prev => ({ ...prev, isDragging: false }));
    }, []);

    // Handle mouse leave
    const handleMouseLeave = useCallback(() => {
        setPan(prev => ({ ...prev, isDragging: false }));
        setHoveredNode(null);
    }, []);

    // Enhanced mouse move to handle pan dragging
    const handleMouseMoveWithPan = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (pan.isDragging) {
            const deltaX = e.clientX - pan.lastX;
            const deltaY = e.clientY - pan.lastY;
            // Only consider it a "move" if we've moved more than 3 pixels
            const hasMoved = pan.hasMoved || Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3;

            if (hasMoved) {
                setPan(prev => ({
                    ...prev,
                    offsetX: prev.offsetX + deltaX,
                    offsetY: prev.offsetY + deltaY,
                    hasMoved: true,
                    lastX: e.clientX,
                    lastY: e.clientY
                }));
            }
            return;
        }
        handleMouseMove(e);
    }, [pan.isDragging, pan.hasMoved, pan.lastX, pan.lastY, handleMouseMove]);

    // Touch handlers for mobile
    const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
        if (e.touches.length === 2) {
            // Pinch zoom start
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
            const centerX = (touch1.clientX + touch2.clientX) / 2;
            const centerY = (touch1.clientY + touch2.clientY) / 2;

            touchStateRef.current = {
                lastTouchDistance: distance,
                lastTouchCenter: { x: centerX, y: centerY }
            };
        } else if (e.touches.length === 1) {
            // Single touch pan start
            const touch = e.touches[0];
            setPan(prev => ({
                ...prev,
                isDragging: true,
                lastX: touch.clientX,
                lastY: touch.clientY
            }));
        }
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();

        if (e.touches.length === 2 && touchStateRef.current.lastTouchDistance !== null) {
            // Pinch zoom
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
            const centerX = (touch1.clientX + touch2.clientX) / 2;
            const centerY = (touch1.clientY + touch2.clientY) / 2;

            const scale = distance / touchStateRef.current.lastTouchDistance;

            setConfig(prev => ({
                ...prev,
                zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom * scale))
            }));

            // Also pan based on center movement
            if (touchStateRef.current.lastTouchCenter) {
                const deltaX = centerX - touchStateRef.current.lastTouchCenter.x;
                const deltaY = centerY - touchStateRef.current.lastTouchCenter.y;
                setPan(prev => ({
                    ...prev,
                    offsetX: prev.offsetX + deltaX,
                    offsetY: prev.offsetY + deltaY
                }));
            }

            touchStateRef.current = {
                lastTouchDistance: distance,
                lastTouchCenter: { x: centerX, y: centerY }
            };
        } else if (e.touches.length === 1 && pan.isDragging) {
            // Single touch pan
            const touch = e.touches[0];
            const deltaX = touch.clientX - pan.lastX;
            const deltaY = touch.clientY - pan.lastY;
            setPan(prev => ({
                ...prev,
                offsetX: prev.offsetX + deltaX,
                offsetY: prev.offsetY + deltaY,
                lastX: touch.clientX,
                lastY: touch.clientY
            }));
        }
    }, [pan.isDragging, pan.lastX, pan.lastY]);

    const handleTouchEnd = useCallback(() => {
        touchStateRef.current = {
            lastTouchDistance: null,
            lastTouchCenter: null
        };
        setPan(prev => ({ ...prev, isDragging: false }));
    }, []);

    // Zoom control functions
    const handleZoomIn = useCallback(() => {
        setConfig(prev => ({
            ...prev,
            zoom: Math.min(MAX_ZOOM, prev.zoom + ZOOM_STEP)
        }));
    }, []);

    const handleZoomOut = useCallback(() => {
        setConfig(prev => ({
            ...prev,
            zoom: Math.max(MIN_ZOOM, prev.zoom - ZOOM_STEP)
        }));
    }, []);

    const handleResetView = useCallback(() => {
        setConfig(prev => ({ ...prev, zoom: 1 }));
        setPan({ offsetX: 0, offsetY: 0, isDragging: false, hasMoved: false, lastX: 0, lastY: 0 });
    }, []);

    // Resize canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const parent = canvas.parentElement;
        if (!parent) return;

        const resizeCanvas = () => {
            const oldWidth = canvas.width;
            const oldHeight = canvas.height;
            canvas.width = parent.clientWidth;
            canvas.height = parent.clientHeight;

            // If size changed significantly, reset initialization to recreate particles
            if (Math.abs(oldWidth - canvas.width) > 10 || Math.abs(oldHeight - canvas.height) > 10) {
                particlesRef.current = [];
                initFrameCountRef.current = 0;
            }
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        return () => window.removeEventListener('resize', resizeCanvas);
    }, []);

    return (
        <div style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: config.lightMode ? '#f8fafc' : '#0a0e27',
            borderRadius: '8px',
            overflow: 'hidden'
        }}>
            <canvas
                ref={canvasRef}
                onMouseMove={handleMouseMoveWithPan}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    cursor: pan.isDragging ? 'grabbing' : 'default',
                    touchAction: 'none' // Prevent browser handling of touch events
                }}
            />

            {/* Controls - Compact */}
            <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                zIndex: 100,
                background: config.lightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(10px)',
                padding: '10px',
                borderRadius: '10px',
                border: config.lightMode ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(100, 116, 139, 0.3)',
            }}>
                {/* Title */}
                <div style={{
                    fontSize: '9px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: config.lightMode ? '#94a3b8' : '#64748b',
                    marginBottom: '6px',
                    fontWeight: 600,
                    textAlign: 'center'
                }}>Controls</div>

                {/* Display row - equal width buttons */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                    <button
                        onClick={() => setConfig(prev => ({ ...prev, showParticles: !prev.showParticles }))}
                        title="Toggle particles"
                        style={{
                            flex: 1,
                            padding: '5px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: config.showParticles ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.1)',
                            color: '#60a5fa',
                            border: config.showParticles ? '1px solid #3b82f6' : '1px solid rgba(59, 130, 246, 0.3)',
                        }}
                    >
                        ✨ {config.showParticles ? 'On' : 'Off'}
                    </button>
                    <button
                        onClick={() => setConfig(prev => ({ ...prev, lightMode: !prev.lightMode }))}
                        title="Toggle theme"
                        style={{
                            flex: 1,
                            padding: '5px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: config.lightMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.1)',
                            color: '#60a5fa',
                            border: config.lightMode ? '1px solid #3b82f6' : '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                    >
                        {config.lightMode ? '☀️ Light' : '🌙 Dark'}
                    </button>
                </div>

                {/* Zoom row */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <button
                        onClick={handleZoomOut}
                        disabled={config.zoom <= MIN_ZOOM}
                        title="Zoom out"
                        style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: 700,
                            cursor: config.zoom <= MIN_ZOOM ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: config.zoom <= MIN_ZOOM ? '#94a3b8' : '#60a5fa',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            opacity: config.zoom <= MIN_ZOOM ? 0.5 : 1
                        }}
                    >
                        −
                    </button>
                    <div style={{
                        flex: 1,
                        textAlign: 'center',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: config.lightMode ? '#64748b' : '#94a3b8'
                    }}>
                        {Math.round(config.zoom * 100)}%
                    </div>
                    <button
                        onClick={handleZoomIn}
                        disabled={config.zoom >= MAX_ZOOM}
                        title="Zoom in"
                        style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: 700,
                            cursor: config.zoom >= MAX_ZOOM ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: config.zoom >= MAX_ZOOM ? '#94a3b8' : '#60a5fa',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            opacity: config.zoom >= MAX_ZOOM ? 0.5 : 1
                        }}
                    >
                        +
                    </button>
                    <button
                        onClick={handleResetView}
                        title="Reset view"
                        style={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#60a5fa',
                            border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                    >
                        ↺
                    </button>
                </div>
            </div>

            {/* Legend */}
            <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                zIndex: 100,
                background: config.lightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(10px)',
                padding: '16px',
                borderRadius: '12px',
                border: config.lightMode ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(100, 116, 139, 0.3)'
            }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#fbbf24'
                        }} />
                        <span style={{ fontSize: '11px', color: config.lightMode ? '#64748b' : '#cbd5e1' }}>Brand Equity</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#10b981'
                        }} />
                        <span style={{ fontSize: '11px', color: config.lightMode ? '#64748b' : '#cbd5e1' }}>Latent Elements</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#fb923c'
                        }} />
                        <span style={{ fontSize: '11px', color: config.lightMode ? '#64748b' : '#cbd5e1' }}>Latent Elements</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#ef4444'
                        }} />
                        <span style={{ fontSize: '11px', color: config.lightMode ? '#64748b' : '#cbd5e1' }}>Latent Elements</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#3b82f6'
                        }} />
                        <span style={{ fontSize: '11px', color: config.lightMode ? '#64748b' : '#cbd5e1' }}>Measured Elements</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#9ca3af'
                        }} />
                        <span style={{ fontSize: '11px', color: config.lightMode ? '#64748b' : '#cbd5e1' }}>Locked</span>
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                zIndex: 100,
                background: config.lightMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(10px)',
                padding: '16px',
                borderRadius: '12px',
                border: config.lightMode ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(100, 116, 139, 0.3)',
                maxWidth: '250px',
                fontSize: '12px',
                color: config.lightMode ? '#64748b' : '#94a3b8',
                lineHeight: 1.6
            }}>
                <strong style={{ color: '#60a5fa' }}>Click</strong> node to view details<br />
                <strong style={{ color: '#60a5fa' }}>Hover</strong> to highlight<br />
                <strong style={{ color: '#60a5fa' }}>Drag</strong> to pan<br />
                <span style={{ fontSize: '11px', opacity: 0.8 }}>Touch: pinch to zoom, drag to pan</span>
            </div>

            {/* Tooltip */}
            <div style={{
                position: 'absolute',
                top: '50px',
                left: '120px',
                width: '250px',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.98)',
                color: '#333',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                pointerEvents: 'none',
                zIndex: 1000,
                boxShadow: config.lightMode ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 4px 12px rgba(0, 0, 0, 0.4)',
                border: config.lightMode ? '1px solid #cbd5e1' : '1px solid #e0e0e0',
                opacity: hoveredNode ? 1 : 0,
                transition: 'opacity 0.2s'
            }}>
                {hoveredNode && (
                    <>
                        <div style={{
                            fontWeight: 'bold',
                            marginBottom: '8px',
                            fontSize: '16px',
                            color: '#1f2937'
                        }}>
                            {hoveredNode.element}
                        </div>
                        {elementDescriptions[hoveredNode.element]?.mouseover && (
                            <div style={{
                                marginBottom: '8px',
                                fontSize: '12px',
                                color: '#4b5563',
                                fontStyle: 'italic'
                            }}>
                                {elementDescriptions[hoveredNode.element].mouseover}
                            </div>
                        )}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <span style={{ color: '#6b7280' }}>Score:</span>
                            {hoveredNode.locked ? (
                                <LockOutlined style={{ fontSize: '28px', color: '#9ca3af' }} />
                            ) : (
                                <span style={{
                                    color: getScoreColor(hoveredNode.element),
                                    fontWeight: 'bold',
                                    fontSize: '24px'
                                }}>
                                    {hoveredNode.score.toFixed(1)}%
                                </span>
                            )}
                        </div>
                        <div style={{
                            marginTop: '8px',
                            paddingTop: '8px',
                            borderTop: '1px solid #e5e7eb',
                            fontSize: '12px',
                            color: '#9ca3af'
                        }}>
                            {getElementTypeLabel(hoveredNode.element)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RadialOrbitalVisualization;